import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { TrainingRegistrationSchema } from "../../fixtures/training-registration-schema";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../Components/form";
import { Input } from "../Components/InputField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../Components/SelectField";
import { ChevronLeft, Loader2 } from "lucide-react";
import { registerForTraining } from "../../redux/api/register-trainee.api";
import Loader from "../Components/Loader";
import { StateList } from "../../fixtures/states";

interface SectionProps {
  children: React.ReactNode;
  sectionTitle: string;
}

const Section = ({ children, sectionTitle }: SectionProps) => {
  return (
    <div className="rounded-md shadow-md p-4 my-3 flex flex-col gap-3">
      <h1 className="text-lg font-semibold text-gray-800">{sectionTitle}</h1>
      <div className="flex-1">{children}</div>
    </div>
  );
};

const ToastAlert = () => {
  return (
    <div role="alert" className="alert alert-error">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-6 w-6 shrink-0 stroke-current"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>
        Something went Wrong while submitting the Form, Please try again!
      </span>
    </div>
  );
};

const TrainingRegistration = () => {
  const form = useForm<z.infer<typeof TrainingRegistrationSchema>>({
    defaultValues: {
      preferred_time: "",
      training_program: "Certificate Course",
      full_name: "",
      email: "",
      phone_number: "",
      whatsapp_number: "",
      date_of_birth: "",
      gender: "",
      city: "",
      building_name: "",
      landmark: "",
      state: "",
      district: "",
      postal_code: "",
    },
    resolver: zodResolver(TrainingRegistrationSchema),
  });

  const [isPending, setIsPending] = useState(false);
  const [isCheckedTnC, setIsCheckedTnC] = useState(false);

  const [isError, setIsError] = useState(false);

  // training registration form submission
  const formSubmit = async (
    values: z.infer<typeof TrainingRegistrationSchema>
  ) => {
    setIsPending(true);
    try {
      const response = await registerForTraining(values);
      if (response.message.toLowerCase() === "success") {
        submitPayUForm(response.data.data);
      }
    } catch (error) {
      setIsError(true);
      console.error(error, "Error while Submitting the Form!");
    } finally {
      setIsPending(false);
      setInterval(() => {
        setIsError(false);
      }, 5000);
    }
  };

  // payU payment gateway form submission
  const submitPayUForm = (formHtml: any) => {
    const div = document.createElement("div");
    div.innerHTML = formHtml;
    const form = div.querySelector("form");
    if (form) {
      document.body.appendChild(div);
      form.submit();
    } else {
      throw new Error("Payment form not found in response");
    }
  };

  return (
    <>
      <div className="py-3 w-[90%] flex justify-end mx-auto">
        <button
          className="text-md bg-sky-300 rounded-xl px-3 py-2 flex gap-1"
          onClick={() => (window.location.href = "/")}
        >
          <ChevronLeft />
          Back
        </button>
      </div>
      <div className="lg:w-[70%] px-3 lg:mx-auto">
        {isPending ? (
          <div className="flex items-center justify-center w-full h-[dvh]">
            <Loader />
          </div>
        ) : (
          <div className="mt-4">
            {isError && <ToastAlert />}
            <h1 className="text-xl md:text-3xl text-gray-700 font-semibold text-center mb-8">
              CERTIFICATE PROGRAMME REGISTRATION FORM
            </h1>
            <Form {...form}>
              <div>
                <form onSubmit={form.handleSubmit(formSubmit)}>
                  <Section sectionTitle="Training Informations">
                    <div className="lg:grid grid-cols-3 gap-4">
                      {/* <FormField
                        control={form.control}
                        name="training_program"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>You are Finally Selected For</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Post" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {["RWO Training", "DWO Training"].map(
                                  (post) => {
                                    return (
                                      <SelectItem key={post} value={post}>
                                        {post}
                                      </SelectItem>
                                    );
                                  }
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      /> */}
                      <FormField
                        control={form.control}
                        name="training_program"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>You're finally Selected For</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Certificate Course"
                                {...field}
                                readOnly
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      {/* <FormField
                        control={form.control}
                        name="preferred_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Time of Classes</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="9 am to 12 pm"
                                {...field}
                                readOnly
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      /> */}
                      <FormField
                        control={form.control}
                        name="preferred_time"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Time of Classes</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Time" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {[
                                  "In Between 9:00 AM - 12:00 PM ",
                                  "In Between 07:00 PM - 10:00 PM",
                                ].map((post) => {
                                  return (
                                    <SelectItem key={post} value={post}>
                                      {post}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>
                  </Section>

                  <Section sectionTitle="Personal Informations">
                    <div className="lg:grid grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="full_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name as per PAN Card</FormLabel>
                            <FormControl>
                              <Input placeholder="John Doe" {...field} />
                            </FormControl>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        defaultValue=""
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="example@gmail.com"
                                {...field}
                                type="email"
                              />
                            </FormControl>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="phone_number"
                        rules={{
                          required: "Contact number is required",
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: "Enter a valid 10-digit number",
                          },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Calling Number</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                placeholder="9812345667"
                                onChange={(e) => {
                                  const value = e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 10);
                                  field.onChange(value);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="whatsapp_number"
                        rules={{
                          required: "WhatsApp number is required",
                          pattern: {
                            value: /^[0-9]{10}$/,
                            message: "Enter a valid 10-digit number",
                          },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>WhatsApp Number</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="tel"
                                inputMode="numeric"
                                maxLength={10}
                                placeholder="9812345667"
                                onChange={(e) => {
                                  const value = e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 10);
                                  field.onChange(value);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="date_of_birth"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Date Of Birth</FormLabel>
                            <FormControl>
                              <Input placeholder="" {...field} type="date" />
                            </FormControl>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="gender"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Gender</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select Gender" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {["Male", "Female", "Other"].map((gender) => {
                                  return (
                                    <SelectItem key={gender} value={gender}>
                                      {gender}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>
                  </Section>

                  <Section sectionTitle="Address Informations">
                    <div>
                      <div className="lg:grid grid-cols-3 gap-4">
                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>City / Town</FormLabel>
                              <FormControl>
                                <Input placeholder="Guwahati" {...field} />
                              </FormControl>
                              <FormMessage />
                              {!field.value && (
                                <FormDescription className="font-semibold text-red-400">
                                  This is a required field
                                </FormDescription>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="building_name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Building Name & House No.</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Building name & house no."
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                              {!field.value && (
                                <FormDescription className="font-semibold text-red-400">
                                  This is a required field
                                </FormDescription>
                              )}
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="landmark"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Land Mark</FormLabel>
                              <FormControl>
                                <Input placeholder="Land Mark" {...field} />
                              </FormControl>
                              <FormMessage />
                              {!field.value && (
                                <FormDescription className="font-semibold text-red-400">
                                  This is a required field
                                </FormDescription>
                              )}
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <div className="lg:grid grid-cols-3 gap-4 mt-3">
                      <FormField
                        control={form.control}
                        name="state"
                        render={({ field: stateField }) => (
                          <FormItem>
                            <FormLabel>State</FormLabel>
                            <Select
                              onValueChange={(value) => {
                                stateField.onChange(value);
                                form.setValue("district", "");
                              }}
                              value={stateField.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select State" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {StateList.map((state) => {
                                  return (
                                    <SelectItem
                                      key={state.state}
                                      value={state.state}
                                    >
                                      {state.state}
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                            {!stateField.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="district"
                        render={({ field: districtField }) => (
                          <FormItem>
                            <FormLabel>Select District</FormLabel>
                            <Select
                              onValueChange={districtField.onChange}
                              value={districtField.value}
                              disabled={!form.getValues("state")}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={
                                      form.getValues("state")
                                        ? "Select District"
                                        : "First select a State"
                                    }
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {form.getValues("state") &&
                                  StateList.find(
                                    (item) =>
                                      item.state === form.getValues("state"),
                                  )?.districts.map((district, index) => (
                                    <SelectItem key={index} value={district}>
                                      {district}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                            {!districtField.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="postal_code"
                        rules={{
                          required: "Postal Code is required",
                          pattern: {
                            value: /^[0-9]{6}$/,
                            message: "Enter a valid 6-digit postal code.",
                          },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Postal Code</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="666009"
                                type="tel"
                                inputMode="numeric"
                                maxLength={6}
                                onChange={(e) => {
                                  const value = e.target.value
                                    .replace(/\D/g, "")
                                    .slice(0, 6);
                                  field.onChange(value);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                            {!field.value && (
                              <FormDescription className="font-semibold text-red-400">
                                This is a required field
                              </FormDescription>
                            )}
                          </FormItem>
                        )}
                      />
                    </div>
                  </Section>

                  <Section sectionTitle="">
                    <div className="my-3 flex items-center justify-center flex-col">
                      <div className="flex flex-col space-y-4">
                        <div className="flex flex-col items-center justify-center">
                          <div className="flex gap-3 items-center justify-center">
                            <input
                              type="checkbox"
                              checked={isCheckedTnC}
                              onChange={(e) =>
                                setIsCheckedTnC(e.target.checked)
                              }
                              className="size-5"
                            />
                            <a
                              href="/terms-conditions"
                              rel="noopenner noreferrer"
                              target="_blank"
                              className="text-blue-600 text-sm md:text-md"
                            >
                              Terms and Conditions
                            </a>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-center my-3">
                        <button
                          className="px-3 py-2 w-[200px] border bg-blue-200 rounded-md shadow-md hover:shadow-gray-400 hover:bg-blue-300 text-lg disabled:bg-blue-50 disabled:text-gray-300 text-blue-800 font-semibold text-center border-blue-500 transition-all duration-300 ease-in-out
                      flex items-center justify-center"
                          disabled={!isCheckedTnC}
                          type="submit"
                        >
                          {isPending ? <Loader2 size={20} /> : "REGISTER NOW"}
                        </button>
                      </div>
                      <div className="flex flex-col items-center justify-center my-4">
                        <a
                          href="/why-training"
                          target="_blank"
                          rel="noopenner noreferrer"
                          className="text-blue-600 text-sm md:text-md"
                        >
                          Why this training?
                        </a>
                        <a
                          href="/why-fee"
                          target="_blank"
                          rel="noopenner noreferrer"
                          className="text-blue-600 text-sm md:text-md"
                        >
                          Why there is a fee for training?
                        </a>
                      </div>
                    </div>
                  </Section>
                </form>
              </div>
            </Form>
          </div>
        )}
      </div>
    </>
  );
};

export default TrainingRegistration;
